import classnames from 'classnames';
import get from 'lodash/get';
import map from 'lodash/map';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
/** import third-party libraries in the section above, keep the ascending order */

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import StarIcon from '@mui/icons-material/Star';
import Tooltip from '@mui/material/Tooltip';
/** import mui components in the section above, keep the ascending order */

import { ButtonGroup, NeutralButton, PrimaryButton } from 'widgets-v5/buttons';
import { GlobalModal } from 'widgets-v5/modal';
import FormField from 'widgets-v5/form-field';
import StandardInput from 'widgets-v5/standard-input';
import StandardSelector from 'widgets-v5/standard-selector';
/** import widgets components in the section above, keep the ascending order */

import { getAdPresetAppliedPropertyLabels } from 'states/resources/ad-presets/business-logic';
import { getDefaultPresetMessage } from 'containers/ad-preset/modules/info-alert-default-ad-preset';
import { getEnvironmentSettings } from 'services/environment';
import { isUsingGuaranteedInventoryType } from 'states/resources/campaigns/business-logic';
import { SELF_SERVE_ROOT_ORG } from 'states/profile/business-rules';
import AdPresetSummary from 'containers/ad-preset/modules/ad-preset-summary';
/** import classes and others in the section above, keep the ascending order */

const ORDER_OPTIONS = [
    {
        label: 'Alphabetical',
        value: 'byName',
    },
    {
        label: 'Last Created',
        value: 'byDate',
    },
];

const AdPresetModal = class extends React.Component {
    static propTypes = {
        adPresets: PropTypes.array,
        campaign: PropTypes.object,
        closeModal: PropTypes.func,
        isModalOpen: PropTypes.bool,
        openModal: PropTypes.func,
        setAdPreset: PropTypes.func,
    };

    state = {
        selectedAdPreset: null,
        searchString: '',
        selectedSort: 'byDate',
    };

    onFieldChange = string => {
        this.setState({
            searchString: string,
        });
    };

    handleDropdownChange = value => {
        this.setState({
            selectedSort: value,
        });
    };

    handleAdPresetClick = adpreset => {
        this.setState({
            selectedAdPreset: adpreset,
        });
    };

    partialHighlight = (text, part) => {
        const splits = text.split(part);

        return map(splits, (split, i) => {
            return (
                <span key={split + i}>
                    {split}
                    {i < splits.length - 1 && (
                        <span className="ef5-ad-preset-modal__body-list-item-highlight">
                            <b>{part}</b>
                        </span>
                    )}
                </span>
            );
        });
    };

    render() {
        const { adPresets, campaign, adPlatforms, ownOrganization, bidStrategyMode } = this.props;
        const { searchString, selectedSort, selectedAdPreset } = this.state;

        if (!adPresets.length) {
            return <div />;
        }

        const isGuaranteedInventory = isUsingGuaranteedInventoryType(campaign);

        const isSelfServeRootOrg = ownOrganization?.id === SELF_SERVE_ROOT_ORG;
        const showDefaultColumn = !isSelfServeRootOrg;

        const filteredAdPresets = _(adPresets)
            .filter(adPreset => new RegExp(searchString, 'gi').test(adPreset.ad_preset_name))
            .sortBy(adPreset => {
                if (selectedSort === 'byName') {
                    return adPreset.ad_preset_name.toLowerCase();
                } else {
                    return -moment(adPreset._created).valueOf();
                }
            })
            .value();

        const platforms = adPlatforms || [];
        // Labels that would survive platform/PG/env/bid-mode filtering — drives Apply enablement and warning copy
        const appliedLabels = selectedAdPreset
            ? getAdPresetAppliedPropertyLabels(platforms, selectedAdPreset, {
                  isGuaranteedInventory,
                  bidStrategyMode,
                  environmentSettings: getEnvironmentSettings(),
              })
            : [];
        // Disable Apply when a selection exists but zero labels survive filtering
        const canApplyPreset = Boolean(selectedAdPreset) && appliedLabels.length > 0;
        const checkedPropertyCount = selectedAdPreset
            ? (selectedAdPreset.ad_preset_properties || []).reduce(
                  (count, group) =>
                      count +
                      (group.properties || []).filter(property => property.checked === true).length,
                  0
              )
            : 0;
        // More checked properties than applyable labels ⇒ some (or all) will be skipped
        const hasSkippedFields = checkedPropertyCount > appliedLabels.length;
        // Partial restriction (can still Apply) vs total restriction (Apply stays disabled)
        const applyRestrictionAlertMessage =
            selectedAdPreset && hasSkippedFields
                ? canApplyPreset
                    ? "Some fields from this preset won't be applied due to platform, inventory, or bid mode restrictions."
                    : "None of this preset's fields can be applied to this ad due to platform, inventory, or bid mode restrictions."
                : null;

        return (
            <div className="ef5-ad-preset-modal">
                <NeutralButton onClick={this.props.openModal} text="Apply Ad Preset" />
                <GlobalModal
                    title="Ad Preset"
                    isOpen={this.props.isModalOpen}
                    onClose={this.props.closeModal}
                    bottomBar={
                        <div className="ef5-ad-preset-modal__footer">
                            <ButtonGroup>
                                <PrimaryButton
                                    text="Apply"
                                    disabled={!canApplyPreset}
                                    onClick={() =>
                                        this.props.setAdPreset(this.state.selectedAdPreset)
                                    }
                                />
                                <NeutralButton text="Cancel" onClick={this.props.closeModal} />
                            </ButtonGroup>
                        </div>
                    }
                >
                    <div className="ef5-ad-preset-modal__body">
                        <div className="ef5-ad-preset-modal__presets-form">
                            <FormField>
                                <div className="ef5-ad-preset-modal__form">
                                    <StandardInput
                                        className="ef5-ad-preset-modal__form-input"
                                        value={''}
                                        placeholder="Search"
                                        onChange={this.onFieldChange}
                                    />
                                    <StandardSelector
                                        clearable={false}
                                        searchable={false}
                                        items={ORDER_OPTIONS}
                                        value={this.state.selectedSort}
                                        onChange={value => this.handleDropdownChange(value)}
                                    />
                                </div>
                            </FormField>
                            <div className="ef5-ad-preset-modal__list">
                                <table className="ef5-ad-preset-modal__table">
                                    <thead>
                                        <tr>
                                            {showDefaultColumn && (
                                                <th className="ef5-ad-preset-modal__table-header ef5-ad-preset-modal__table-header--default">
                                                    Default Preset
                                                </th>
                                            )}
                                            <th className="ef5-ad-preset-modal__table-header">
                                                Name
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {map(filteredAdPresets, (adPreset, index) => {
                                            const adPresetName = adPreset.ad_preset_name;
                                            const isDefaultPreset = adPreset.is_default_preset;
                                            return (
                                                <tr
                                                    key={'line-' + index}
                                                    className={classnames(
                                                        'ef5-ad-preset-modal__table-row',
                                                        get(selectedAdPreset, 'id') === adPreset.id
                                                            ? 'ef5-ad-preset-modal__table-row--active'
                                                            : ''
                                                    )}
                                                    onClick={() =>
                                                        this.handleAdPresetClick(adPreset)
                                                    }
                                                >
                                                    {showDefaultColumn && (
                                                        <td className="ef5-ad-preset-modal__table-cell ef5-ad-preset-modal__table-cell--default">
                                                            {isDefaultPreset && (
                                                                <Tooltip
                                                                    title={
                                                                        <span
                                                                            style={{
                                                                                fontSize: '14px',
                                                                            }}
                                                                        >
                                                                            {getDefaultPresetMessage(
                                                                                {
                                                                                    defaultPresetName: adPresetName,
                                                                                    isSelfServeRootOrg,
                                                                                }
                                                                            )}
                                                                        </span>
                                                                    }
                                                                >
                                                                    <StarIcon className="ef5-ad-preset-modal__star-icon" />
                                                                </Tooltip>
                                                            )}
                                                            {!isDefaultPreset && '-'}
                                                        </td>
                                                    )}
                                                    <td className="ef5-ad-preset-modal__table-cell ef5-ad-preset-modal__table-cell--name">
                                                        {this.partialHighlight(
                                                            adPresetName,
                                                            searchString
                                                        )}
                                                        <i className="fa fa-check" />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="ef5-ad-preset-modal__summary">
                            {selectedAdPreset ? (
                                <React.Fragment>
                                    {applyRestrictionAlertMessage && (
                                        <Box sx={{ mb: 2 }}>
                                            <Alert severity="warning">
                                                {applyRestrictionAlertMessage}
                                            </Alert>
                                        </Box>
                                    )}
                                    <AdPresetSummary
                                        hiddinHeaderText={true}
                                        formData={selectedAdPreset}
                                        shouldHideFlexSegments={campaign.shouldHideFlexSegments}
                                        adPlatforms={adPlatforms}
                                        doohPublishers={this.props.doohPublishers}
                                        isGuaranteedInventory={isGuaranteedInventory}
                                        bidStrategyMode={bidStrategyMode}
                                    />
                                </React.Fragment>
                            ) : null}
                        </div>
                    </div>
                </GlobalModal>
            </div>
        );
    }
};

const selector = state => ({
    doohPublishers: state?.resources?.doohPublishers || [],
    ownOrganization: state?.resources?.ownOrganization,
});

export default connect(selector)(AdPresetModal);
